import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from '@/integrations/supabase/client';

interface JobApplicationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  experience: string;
  coverLetter: string;
  resume: FileList;
}

const JobApplicationForm = ({ open, onOpenChange }: JobApplicationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('email', data.email);
      formData.append('phone', data.phone || '');
      formData.append('position', data.position);
      formData.append('experienceLevel', data.experience);
      formData.append('coverLetter', data.coverLetter || '');
      
      if (data.resume && data.resume[0]) {
        formData.append('resume', data.resume[0]);
      }

      const response = await fetch(`https://axqzenckmooiqqippzib.supabase.co/functions/v1/submit-job-application`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4cXplbmNrbW9vaXFxaXBwemliIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY3MTc2OTMsImV4cCI6MjA3MjI5MzY5M30.aKmdnNOmt7yME9FvkwXZoSDy2WH660GEYXaxPOB-NsA`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }
      
      toast.success(t("job.success"));
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting job application:', error);
      toast.error(t("job.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("job.title")}</DialogTitle>
          <DialogDescription>
            {t("job.description")}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t("job.firstName")} *</Label>
              <Input
                id="firstName"
                {...register("firstName", { required: t("job.errors.firstName") })}
                placeholder={t("job.placeholder.firstName")}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastName">{t("job.lastName")} *</Label>
              <Input
                id="lastName"
                {...register("lastName", { required: t("job.errors.lastName") })}
                placeholder={t("job.placeholder.lastName")}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("job.email")} *</Label>
            <Input
              id="email"
              type="email"
              {...register("email", { 
                required: t("job.errors.email"),
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: t("job.errors.emailInvalid")
                }
              })}
              placeholder={t("job.placeholder.email")}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t("job.phone")}</Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              placeholder={t("job.placeholder.phone")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">{t("job.position")}</Label>
            <Select onValueChange={(value) => setValue("position", value)}>
              <SelectTrigger>
                <SelectValue placeholder={t("job.placeholder.position")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="operations">{t("job.positions.operations")}</SelectItem>
                <SelectItem value="revenue">{t("job.positions.revenue")}</SelectItem>
                <SelectItem value="guest-services">{t("job.positions.guestServices")}</SelectItem>
                <SelectItem value="housekeeping">{t("job.positions.housekeeping")}</SelectItem>
                <SelectItem value="maintenance">{t("job.positions.maintenance")}</SelectItem>
                <SelectItem value="food-beverage">{t("job.positions.foodBeverage")}</SelectItem>
                <SelectItem value="management">{t("job.positions.management")}</SelectItem>
                <SelectItem value="other">{t("job.positions.other")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="experience">{t("job.experience")} *</Label>
            <Select onValueChange={(value) => setValue("experience", value)}>
              <SelectTrigger>
                <SelectValue placeholder={t("job.placeholder.experience")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-1">{t("job.experience.0-1")}</SelectItem>
                <SelectItem value="2-5">{t("job.experience.2-5")}</SelectItem>
                <SelectItem value="6-10">{t("job.experience.6-10")}</SelectItem>
                <SelectItem value="10+">{t("job.experience.10+")}</SelectItem>
              </SelectContent>
            </Select>
            {errors.experience && (
              <p className="text-sm text-red-500">{t("job.errors.experience")}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="resume">{t("job.resume")} *</Label>
            <Input
              id="resume"
              type="file"
              accept=".pdf,.doc,.docx"
              {...register("resume", { required: t("job.errors.resume") })}
            />
            <p className="text-sm text-gray-500">{t("job.fileFormats")}</p>
            {errors.resume && (
              <p className="text-sm text-red-500">{errors.resume.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="coverLetter">{t("job.coverLetter")}</Label>
            <Textarea
              id="coverLetter"
              {...register("coverLetter")}
              placeholder={t("job.placeholder.coverLetter")}
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t("job.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="beige"
            >
              {isSubmitting ? t("job.submitting") : t("job.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationForm;